// Messaging query layer. Pairs with `lib/mutations/messaging.ts`.

import { useEffect, useRef } from 'react';
import {
  keepPreviousData,
  queryOptions,
  infiniteQueryOptions,
  useQuery,
  useInfiniteQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { ConversationId, UserId } from '@leanin/shared/ids';
import type { components } from '@leanin/api-client';
import { getApiClient } from '../api-client';
import { unwrap } from '../api-error';

// Directory search result shape for the messages "new message" / "add to
// group" pickers. Same shape the previous `searchDirectoryAction` returned.
export type DirectoryMemberRow = {
  id: string;
  name: string;
  subtitle: string;
  photo?: string;
};

export type ConversationSummary = components['schemas']['ConversationSummary'];
export type ConversationsPage = components['schemas']['ConversationsPage'];
export type ConversationDetail = components['schemas']['ConversationDetail'];
export type ConversationDetailEnvelope =
  components['schemas']['ConversationDetailEnvelope'];
export type ConversationScopeCounts =
  components['schemas']['ConversationScopeCounts'];
export type DmReachability = components['schemas']['DmReachability'];
export type MessageView = components['schemas']['MessageView'];
export type MessagesPage = components['schemas']['MessagesPage'];
export type BlocksPage = components['schemas']['BlocksPage'];
export type ScheduledMessageView =
  components['schemas']['ScheduledMessageView'];
export type ScheduledMessagesPage =
  components['schemas']['ScheduledMessagesPage'];

export type ListConversationsInput = {
  pageSize?: number;
  kind?: 'dm' | 'group';
  q?: string;
  unreadOnly?: boolean;
  /** When true, list the viewer's received PENDING message requests instead
   *  of the inbox. `q` / `unreadOnly` only apply to the inbox; the server
   *  ignores them for the requests scope. */
  requests?: boolean;
  /** When true, list only the viewer's ARCHIVED conversations (the "Archived"
   *  view); when false/absent, the inbox excludes archived rows. Applies to
   *  the inbox scope only — the server ignores it for the requests scope. */
  archived?: boolean;
};

// New-message thread polling cadence. 5s matches the existing SSE-replacement
// budget — fast enough that a reply lands in roughly one breath, slow enough
// that an idle thread doesn't hammer the API. `staleTime: 0` on the messages
// query keeps `refetchInterval` honest.
export const MESSAGES_POLL_INTERVAL_MS = 5_000;

// App-wide inbox probe cadence (see `useInboxWatcher`). 30s matches the
// notifications bell. Surfaces that show conversation rows (the Messages page,
// the open dock list) additionally poll the probe at the thread cadence so a
// new message lands in the list as fast as it lands in an open thread.
export const SCOPE_COUNTS_POLL_MS = 30_000;

// Thread page size. A module constant (decision 5b) so it never segments the
// cursor-paginated messages cache: it is written inline in the `messages` leaf
// `queryFn`, absent from the key. Including it would split the cache and break
// prefix matching for cross-variant operations like optimistic message append.
const MESSAGES_PAGE_SIZE = 50;

// Placeholder id for the nullable-id hooks (`useConversation`,
// `useInfiniteMessages`): the leaf factory needs a `ConversationId` to build a
// branded queryOptions, but the read is gated off via `enabled: false` when the
// real id is null, so the placeholder key is never fetched against.
const EMPTY_CONVERSATION_ID = '' as ConversationId;

// Same placeholder-id device as EMPTY_CONVERSATION_ID, for the nullable-id
// `useDmReachability` hook: the leaf needs a branded UserId to key the query,
// but the read is gated off via `enabled: false` when the real id is null.
const EMPTY_USER_ID = '' as UserId;

// ---- Client fetchers ----

// All fetchers unwrap through `unwrap` (not bare `throwApiError(error)`) so
// the HTTP status rides along and a 404 throws the typed `NotFoundApiError` —
// the global retry predicate exempts that type, so a genuinely-gone
// conversation fails once instead of burning three retries per refetch.
export async function fetchConversations(
  params: ListConversationsInput & { cursor?: string },
): Promise<ConversationsPage> {
  const client = getApiClient();
  return unwrap(
    await client.GET('/api/v1/conversations', {
      params: {
        query: {
          pageSize: params.pageSize,
          cursor: params.cursor,
          kind: params.kind,
          q: params.q,
          ...(params.unreadOnly !== undefined
            ? { unread: params.unreadOnly ? 'true' : 'false' }
            : {}),
          ...(params.requests !== undefined
            ? { requests: params.requests ? 'true' : 'false' }
            : {}),
          ...(params.archived !== undefined
            ? { archived: params.archived ? 'true' : 'false' }
            : {}),
        },
      },
    }),
  );
}

export async function fetchConversation(
  id: ConversationId,
): Promise<ConversationDetailEnvelope> {
  const client = getApiClient();
  return unwrap(
    await client.GET('/api/v1/conversations/{id}', {
      params: { path: { id } },
    }),
  );
}

export async function fetchConversationMessages(params: {
  conversationId: ConversationId;
  cursor?: string;
  pageSize: number;
}): Promise<MessagesPage> {
  const client = getApiClient();
  return unwrap(
    await client.GET('/api/v1/conversations/{id}/messages', {
      params: {
        path: { id: params.conversationId },
        query: {
          pageSize: params.pageSize,
          cursor: params.cursor,
        },
      },
    }),
  );
}

export async function fetchConversationScopeCounts(
  q: string | null,
): Promise<ConversationScopeCounts> {
  const client = getApiClient();
  return unwrap(
    await client.GET('/api/v1/conversations/scope-counts', {
      params: {
        query: {
          ...(q !== null ? { q } : {}),
        },
      },
    }),
  );
}

export async function fetchDmReachability(
  userId: UserId,
): Promise<DmReachability> {
  const client = getApiClient();
  return unwrap(
    await client.GET('/api/v1/conversations/dm-reachability', {
      params: { query: { userId } },
    }),
  );
}

export async function fetchBlocks(input: {
  cursor?: string;
  pageSize?: number;
}): Promise<BlocksPage> {
  const client = getApiClient();
  return unwrap(
    await client.GET('/api/v1/blocks', {
      params: {
        query: {
          pageSize: input.pageSize,
          cursor: input.cursor,
        },
      },
    }),
  );
}

export async function fetchDirectory(
  query: string,
  sharedCommunityOnly = false,
): Promise<readonly DirectoryMemberRow[]> {
  const client = getApiClient();
  const { data } = await client.GET('/api/v1/members', {
    params: {
      query: {
        pageSize: 20,
        sort: 'connection',
        ...(query.length > 0 ? { q: query } : {}),
        ...(sharedCommunityOnly ? { sharedCommunityOnly: 'true' as const } : {}),
      },
    },
  });
  if (data === undefined) return [];
  return data.items.map((m) => {
    const subtitle = [m.jobTitle, m.company]
      .filter((s): s is string => typeof s === 'string' && s.length > 0)
      .join(' · ');
    const out: DirectoryMemberRow = {
      id: m.id,
      name: [m.firstName, m.lastName]
        .filter((s): s is string => typeof s === 'string' && s.length > 0)
        .join(' '),
      subtitle,
    };
    if (m.photoUrl !== undefined && m.photoUrl !== null) {
      out.photo = m.photoUrl;
    }
    return out;
  });
}

// The viewer's own pending (not-yet-dispatched, not-cancelled) scheduled
// messages. The server filters to pending + caller-owned rows; pass
// `conversationId` to scope to a single thread (the inline panel does this so
// it never misses a thread's rows behind a whole-inbox page cap).
export async function fetchScheduledMessages(
  input: { pageSize?: number; conversationId?: string } = {},
): Promise<ScheduledMessagesPage> {
  const client = getApiClient();
  return unwrap(
    await client.GET('/api/v1/scheduled', {
      params: {
        query: {
          pageSize: input.pageSize,
          conversationId: input.conversationId,
        },
      },
    }),
  );
}

// ---- Query keys + leaves ----
//
// Inner nodes (`all`, `conversations`) stay bare `as const` arrays — they are
// invalidation prefixes. Leaves return queryOptions / infiniteQueryOptions and
// normalize their raw args ONCE so the cache key and the fetch call derive from
// the same object (decision 3).

export const messagingKeys = {
  all: ['messaging'] as const,
  conversations: () => [...messagingKeys.all, 'conversations'] as const,

  conversationsList: (raw: ListConversationsInput) => {
    const args: ListConversationsInput = {
      ...(raw.pageSize !== undefined ? { pageSize: raw.pageSize } : {}),
      ...(raw.kind !== undefined ? { kind: raw.kind } : {}),
      ...(raw.q !== undefined ? { q: raw.q.trim() } : {}),
      ...(raw.unreadOnly !== undefined ? { unreadOnly: raw.unreadOnly } : {}),
      ...(raw.requests !== undefined ? { requests: raw.requests } : {}),
      ...(raw.archived !== undefined ? { archived: raw.archived } : {}),
    };
    return queryOptions({
      queryKey: [...messagingKeys.conversations(), 'list', args] as const,
      queryFn: () => fetchConversations(args),
      // Match the directory + infinite-list leaves: keep the prior page's rows
      // while a new `q` refetches so the compose picker's results don't blink
      // out between keystrokes.
      placeholderData: keepPreviousData,
    });
  },

  conversationsInfinite: (raw: ListConversationsInput) => {
    const args: ListConversationsInput = {
      ...(raw.pageSize !== undefined ? { pageSize: raw.pageSize } : {}),
      ...(raw.kind !== undefined ? { kind: raw.kind } : {}),
      ...(raw.q !== undefined ? { q: raw.q.trim() } : {}),
      ...(raw.unreadOnly !== undefined ? { unreadOnly: raw.unreadOnly } : {}),
      ...(raw.requests !== undefined ? { requests: raw.requests } : {}),
      ...(raw.archived !== undefined ? { archived: raw.archived } : {}),
    };
    return infiniteQueryOptions({
      queryKey: [...messagingKeys.conversations(), 'list', args] as const,
      queryFn: ({ pageParam }) =>
        fetchConversations({ ...args, cursor: pageParam ?? undefined }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      placeholderData: keepPreviousData,
    });
  },

  conversation: (id: ConversationId) =>
    queryOptions({
      queryKey: [...messagingKeys.all, 'conversation', id] as const,
      queryFn: () => fetchConversation(id),
    }),

  // Prefix over every `q` variant, for invalidation.
  conversationScopeCountsAll: () =>
    [...messagingKeys.all, 'conversation-scope-counts'] as const,

  conversationScopeCounts: (rawQ?: string) => {
    const q = rawQ ?? null;
    return queryOptions({
      queryKey: [...messagingKeys.conversationScopeCountsAll(), q] as const,
      queryFn: () => fetchConversationScopeCounts(q),
    });
  },

  // PageSize is a module constant in the queryFn, NOT in the key (decision 5b)
  // — it's a fetch parameter, not part of the conversation's message-thread
  // identity. Cursor is injected via pageParam, never in the key. Keeping the
  // key shape `[...all,'messages',conversationId]` is what lets the optimistic
  // message append in mutations/messaging.ts prefix-match every page variant.
  messages: (conversationId: ConversationId) =>
    infiniteQueryOptions({
      queryKey: [...messagingKeys.all, 'messages', conversationId] as const,
      queryFn: ({ pageParam }) =>
        fetchConversationMessages({
          conversationId,
          pageSize: MESSAGES_PAGE_SIZE,
          cursor: pageParam ?? undefined,
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      staleTime: 0,
      refetchInterval: MESSAGES_POLL_INTERVAL_MS,
    }),

  directory: (rawQuery: string, sharedCommunityOnly = false) => {
    const q = rawQuery.trim();
    return queryOptions({
      // The flag is part of the key: the compose "+" (scoped) and the group
      // Add-members picker (broad) both fetch the empty `q` list, and must not
      // share a cache entry.
      queryKey: [...messagingKeys.all, 'directory', q, sharedCommunityOnly] as const,
      queryFn: () => fetchDirectory(q, sharedCommunityOnly),
      staleTime: 30 * 1000,
      placeholderData: keepPreviousData,
    });
  },

  dmReachability: (userId: UserId) =>
    queryOptions({
      queryKey: [...messagingKeys.all, 'dm-reachability', userId] as const,
      queryFn: () => fetchDmReachability(userId),
      staleTime: 30 * 1000,
    }),

  blocks: (raw: { cursor?: string; pageSize?: number } = {}) => {
    const args = {
      ...(raw.pageSize !== undefined ? { pageSize: raw.pageSize } : {}),
    };
    return queryOptions({
      queryKey: [...messagingKeys.all, 'blocks', args] as const,
      queryFn: () => fetchBlocks(args),
    });
  },

  // Shared prefix for every per-conversation scheduled list. The schedule /
  // cancel mutations invalidate and `removeItemFromLists` prefix-match on THIS
  // key so they reach every conversation's cached list in one call — the single
  // source of the `'scheduled'` leaf, referenced by the mutations too.
  scheduledKey: () => [...messagingKeys.all, 'scheduled'] as const,

  // Single-page list (no cursor) scoped to one conversation — the server
  // filters by `conversationId`, so the panel never misses rows behind a
  // whole-inbox page cap.
  scheduled: (conversationId: string) =>
    queryOptions({
      queryKey: [...messagingKeys.scheduledKey(), conversationId] as const,
      queryFn: () => fetchScheduledMessages({ conversationId }),
    }),
};

// ---- Hooks ----

// Single-page conversation list. `opts.enabled` gates the fetch so callers
// that only search on a typed query (the compose picker) never hit the inbox
// with an empty `q`, which would return the whole conversation list.
export function useConversations(
  input: ListConversationsInput,
  opts: { enabled?: boolean } = {},
) {
  return useQuery({
    ...messagingKeys.conversationsList(input),
    ...(opts.enabled !== undefined ? { enabled: opts.enabled } : {}),
  });
}

export function useInfiniteConversations(input: ListConversationsInput) {
  return useInfiniteQuery(messagingKeys.conversationsInfinite(input));
}

// Received pending message requests (`requests=true`). A thin wrapper over
// `conversationsInfinite` that pins `requests: true` so the Requests tab and
// the inbox stay distinct cache entries (the key carries `requests` only when
// set, so the inbox key is unchanged). `q` / `unreadOnly` don't apply to the
// requests scope and are intentionally not forwarded.
export function useInfiniteConversationRequests(
  input: { pageSize?: number } = {},
  opts: { enabled?: boolean } = {},
) {
  return useInfiniteQuery({
    ...messagingKeys.conversationsInfinite({ ...input, requests: true }),
    ...(opts.enabled !== undefined ? { enabled: opts.enabled } : {}),
  });
}

export function useConversation(
  id: ConversationId | null,
  opts: { enabled?: boolean } = {},
) {
  // Guard at the hook (decision 3 permits this for nullable ids). The leaf is
  // the single source of key + fetch; `enabled: id !== null` gates the read so
  // the queryFn never runs with the placeholder id. Building the leaf with a
  // placeholder when null (rather than a fallback union key) keeps the branded
  // queryOptions type intact — a `?? guardKey` union breaks TanStack inference.
  //
  // `opts.enabled: false` suspends fetching while KEEPING the id-keyed cache
  // entry rendering — the caller's device for a conversation the viewer has
  // just retired from (declined/reported request) that must stay on screen
  // until the confirmation dialog is dismissed. Passing `null` instead would
  // switch to the placeholder key and blank the pane mid-dialog.
  return useQuery({
    ...messagingKeys.conversation(id ?? EMPTY_CONVERSATION_ID),
    enabled: id !== null && (opts.enabled ?? true),
  });
}

// Paginated messages for a thread. Polling replaces the previous SSE stream:
// `refetchInterval: MESSAGES_POLL_INTERVAL_MS` re-fetches the first page on a
// 5s cadence so newly-delivered peer messages appear without per-thread SSE
// wiring. The polling pauses automatically when the tab is hidden
// (`refetchIntervalInBackground` is left at its default `false`).
export function useInfiniteMessages(
  conversationId: ConversationId | null,
  opts: { enabled?: boolean } = {},
) {
  // Guard at the hook (decision 3 permits this for nullable ids). The leaf is
  // the single source of the load-bearing key (no pageSize, decision 5b), the
  // cursor wiring, and the `staleTime: 0` + `refetchInterval` polling options
  // (decision 7). `enabled: id !== null` gates the read so the queryFn never
  // runs with the placeholder id; polling is suppressed in the same case.
  //
  // `opts.enabled: false` keeps the cached thread rendering but stops the 5s
  // poll — for a retired request kept on screen under its confirmation dialog,
  // where every poll would 404 (see `useConversation`).
  const enabled = conversationId !== null && (opts.enabled ?? true);
  return useInfiniteQuery({
    ...messagingKeys.messages(conversationId ?? EMPTY_CONVERSATION_ID),
    enabled,
    refetchInterval: enabled ? MESSAGES_POLL_INTERVAL_MS : false,
  });
}

export function useConversationScopeCounts(
  q?: string,
  options?: Omit<
    UseQueryOptions<ConversationScopeCounts, Error, ConversationScopeCounts>,
    'queryKey' | 'queryFn'
  >,
) {
  // Anchored on the leaf's key but with the plain fetcher spelled out so the
  // optional wide-typed call-site `options` (queryKey generic `unknown[]`)
  // merges without the narrow-tuple branding clash a leaf spread produces —
  // the same shape `useUnreadCount` uses.
  return useQuery({
    queryKey: messagingKeys.conversationScopeCounts(q).queryKey,
    queryFn: () => fetchConversationScopeCounts(q ?? null),
    ...options,
  });
}

// The inbox's single freshness owner, mounted once by the Shell. It polls the
// cheap scope-counts probe (which lights the sidebar dot and the dock badge)
// and, whenever the probe's `inboxVersion` moves, refetches every
// conversation list. The badge and the rows it points at therefore change on
// the same observation — no list can sit stale behind a lit unread dot.
// Mounted lists refetch now; unmounted tab/search variants are marked stale
// and refetch when next shown. `cancelRefetch: false` joins a list refetch a
// mutation already started instead of restarting it.
export function useInboxWatcher() {
  const queryClient = useQueryClient();
  const query = useConversationScopeCounts(undefined, {
    refetchInterval: SCOPE_COUNTS_POLL_MS,
    refetchOnWindowFocus: true,
  });
  const inboxVersion = query.data?.inboxVersion;
  const seenVersion = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (inboxVersion === undefined) return;
    if (seenVersion.current !== undefined && seenVersion.current !== inboxVersion) {
      void queryClient.invalidateQueries(
        { queryKey: messagingKeys.conversations() },
        { cancelRefetch: false },
      );
    }
    seenVersion.current = inboxVersion;
  }, [inboxVersion, queryClient]);
  return query;
}

// Directory search for the messages pickers. The /members endpoint with
// `sort: 'connection'` ranks people the viewer follows or who follow them
// ahead of others, and the server applies the same reachability + not-blocked
// filter the DM gate does — so every row returned here is safe to start a 1:1
// with. Pass `sharedCommunityOnly` to restrict the no-query empty state to
// people the viewer shares a Circle or Network with (#288): the compose "+"
// modal opts in; the group Add-members picker does not. A search query always
// broadens back to the full reachable directory regardless of the flag.
export function useDirectory(query: string, sharedCommunityOnly = false) {
  return useQuery(messagingKeys.directory(query, sharedCommunityOnly));
}

// Whether starting a 1:1 with `userId` would create a pending message request
// for the viewer right now (relationship gate on for them, not staff, no shared
// Circle/Network). Drives the compose "message request" notice. The caller
// passes `null` to suppress the fetch entirely — when the gate is off for the
// viewer, or when the selection isn't exactly one recipient — so the notice is
// only ever computed when it could actually be shown.
export function useDmReachability(userId: UserId | null) {
  return useQuery({
    ...messagingKeys.dmReachability(userId ?? EMPTY_USER_ID),
    enabled: userId !== null,
  });
}

export function useBlocks(input: { cursor?: string; pageSize?: number } = {}) {
  return useQuery(messagingKeys.blocks(input));
}

// The viewer's own pending scheduled messages for one conversation. The inline
// thread panel reads this for the open conversation.
export function useScheduledMessages(conversationId: string) {
  return useQuery(messagingKeys.scheduled(conversationId));
}

// Re-export the messaging-domain id types so consumers can import either ids
// or query helpers from a single place when convenient.
export type { ConversationId, UserId };
